import React from 'react';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { ButtonProps } from '@/types';

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  className,
  type = 'button',
  as,
  to,
  href,
  onClick,
  ...props
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary:
      'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 active:bg-primary-800',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 active:bg-gray-400',
    outline:
      'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500 active:bg-primary-100',
    ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500 active:bg-gray-200',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 active:bg-red-800',
  };

  const sizes = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg',
  };

  const isDisabled = disabled || loading;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDisabled && onClick) {
      onClick(e as React.MouseEvent<HTMLButtonElement>);
    }
  };

  const Component = as || 'button';
  const componentProps: Record<string, unknown> = {
    className: clsx(baseClasses, variants[variant], sizes[size], className),
    onClick: handleClick,
    ...props,
  };

  // Add appropriate props based on component type
  if (Component === 'button') {
    componentProps.type = type;
    componentProps.disabled = isDisabled;
  } else if (to) {
    componentProps.to = to;
  } else if (href) {
    componentProps.href = href;
  }

  return (
    <Component {...componentProps}>
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          {typeof children === 'string' ? 'Loading...' : children}
        </>
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <span className={clsx('flex-shrink-0', children && 'mr-2')}>{icon}</span>
          )}

          {children}

          {icon && iconPosition === 'right' && (
            <span className={clsx('flex-shrink-0', children && 'ml-2')}>{icon}</span>
          )}
        </>
      )}
    </Component>
  );
};

export default Button;
